/**
 * 按屏幕像素密度选中文字体：低密度走微软雅黑，高密度走 MiSans。
 *
 * MiSans 出厂就没有 TrueType hinting（`fpgm` / `cvt ` 表缺失、字形指令为 0），
 * `dpr = 1` 时笔画无法对齐像素网格，小字号发虚起锯齿；微软雅黑有完整手工 hinting，
 * 同样条件下锐利。密度到 1.25 以上时一个 CSS 像素铺 1.5 个以上物理像素，差别消失。
 *
 * 判据用 `devicePixelRatio` 而不是屏幕分辨率：`screen.width` 返回 CSS 像素
 * （4K@200% 与 1080p@100% 都是 1920），拿不到物理分辨率。详见台账 UI-11。
 */

/** 高密度阈值。1080p@100% 是 1，2K 开 125% / 150% 系统缩放是 1.25 / 1.5。 */
export const HIGH_DENSITY_MIN_DPR = 1.25

export type FontDensity = 'high' | 'low'

/**
 * 把像素密度归到两档。
 *
 * Args:
 *     dpr: `window.devicePixelRatio`。非有限值或非正数按低密度处理 —— 那一档用雅黑，
 *         在任何密度下都不会更差。
 */
export function densityOf(dpr: number): FontDensity {
  if (!Number.isFinite(dpr) || dpr <= 0) return 'low'
  return dpr >= HIGH_DENSITY_MIN_DPR ? 'high' : 'low'
}

/**
 * 把密度写到根元素上供 CSS 选择器消费，启动时调用一次。
 *
 * 不监听 `resize` 或 `matchMedia`：中文字体在页面运行期间跳变比选错一档更难受，
 * 而密度只会因为换屏幕或改浏览器缩放而变，那两件事都会重新走一次这里。
 */
export function applyFontDensity(
  root: HTMLElement = document.documentElement,
  dpr: number = window.devicePixelRatio,
): FontDensity {
  const density = densityOf(dpr)
  root.dataset.fontDensity = density
  return density
}
